import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { ChevronLeft, ChevronRight, Coins, Edit2 } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/Card.tsx";
import { Button } from "@/components/ui/Button.tsx";
import { Input } from "@/components/ui/Input.tsx";
import { Select } from "@/components/ui/Select.tsx";
import { Badge } from "@/components/ui/Badge.tsx";
import { Modal } from "@/components/ui/Modal.tsx";
import { Spinner } from "@/components/ui/Spinner.tsx";
import { adminApi } from "@/api/admin.ts";
import type { ApiError } from "@/api/client.ts";

const PAGE_SIZE = 25;

export function AdminUsersPage() {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const [planFilter, setPlanFilter] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [offset, setOffset] = useState(0);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ["admin", "users", { search, plan: planFilter, role: roleFilter, offset }],
    queryFn: () =>
      adminApi.listUsers({
        limit: PAGE_SIZE,
        offset,
        search: search || undefined,
        plan: planFilter || undefined,
        role: roleFilter || undefined,
      }),
  });

  return (
    <div className="mx-auto max-w-6xl p-8">
      <h1 className="mb-2 text-3xl font-bold text-fg">User Management</h1>
      <p className="mb-6 text-fgMuted">{data?.pagination.total ?? 0} total users</p>

      {/* Filters */}
      <div className="mb-6 flex flex-wrap items-end gap-3">
        <div className="flex-1 min-w-[200px]">
          <Input
            placeholder="Search by email…"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setOffset(0); }}
          />
        </div>
        <div className="w-40">
          <Select
            value={planFilter}
            onChange={(e) => { setPlanFilter(e.target.value); setOffset(0); }}
            placeholder="All plans"
            options={[
              { value: "", label: "All plans" },
              { value: "starter", label: "Starter" },
              { value: "pro", label: "Pro" },
              { value: "agency", label: "Agency" },
            ]}
          />
        </div>
        <div className="w-40">
          <Select
            value={roleFilter}
            onChange={(e) => { setRoleFilter(e.target.value); setOffset(0); }}
            placeholder="All roles"
            options={[
              { value: "", label: "All roles" },
              { value: "user", label: "User" },
              { value: "admin", label: "Admin" },
              { value: "superadmin", label: "Super Admin" },
            ]}
          />
        </div>
      </div>

      {/* Users table */}
      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="flex justify-center py-12"><Spinner /></div>
          ) : !data || data.users.length === 0 ? (
            <p className="py-12 text-center text-fgMuted">No users found</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border text-left text-xs font-medium uppercase tracking-wider text-fgMuted">
                    <th className="px-6 py-3">Email</th>
                    <th className="px-4 py-3">Role</th>
                    <th className="px-4 py-3">Plan</th>
                    <th className="px-4 py-3">Joined</th>
                    <th className="px-4 py-3"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {data.users.map((user) => (
                    <tr key={user.id} className="hover:bg-surface2/30 transition-colors">
                      <td className="px-6 py-3 font-medium text-fg">{user.email}</td>
                      <td className="px-4 py-3">
                        <Badge variant={user.role === "superadmin" ? "danger" : user.role === "admin" ? "warning" : "muted"}>
                          {user.role}
                        </Badge>
                      </td>
                      <td className="px-4 py-3">
                        <Badge variant={user.currentPlan ? "info" : "muted"}>
                          {user.currentPlan ?? "none"}
                        </Badge>
                      </td>
                      <td className="px-4 py-3 text-fgMuted">{new Date(user.createdAt).toLocaleDateString()}</td>
                      <td className="px-4 py-3">
                        <Button variant="ghost" size="sm" onClick={() => setSelectedUserId(user.id)}>
                          <Edit2 className="h-3.5 w-3.5" /> Manage
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      {data && data.pagination.total > PAGE_SIZE && (
        <div className="mt-4 flex items-center justify-between">
          <p className="text-sm text-fgMuted">
            {offset + 1}–{Math.min(offset + PAGE_SIZE, data.pagination.total)} of {data.pagination.total}
          </p>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}>
              <ChevronLeft className="h-4 w-4" /> Prev
            </Button>
            <Button variant="outline" size="sm" disabled={offset + PAGE_SIZE >= data.pagination.total} onClick={() => setOffset(offset + PAGE_SIZE)}>
              Next <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      {/* User detail modal */}
      {selectedUserId && (
        <UserDetailModal
          userId={selectedUserId}
          onClose={() => setSelectedUserId(null)}
          onUpdate={() => queryClient.invalidateQueries({ queryKey: ["admin", "users"] })}
        />
      )}
    </div>
  );
}

function UserDetailModal({ userId, onClose, onUpdate }: { userId: string; onClose: () => void; onUpdate: () => void }) {
  const queryClient = useQueryClient();
  const [creditDelta, setCreditDelta] = useState("");
  const [creditReason, setCreditReason] = useState("");

  const { data, isLoading } = useQuery({
    queryKey: ["admin", "users", userId],
    queryFn: () => adminApi.getUser(userId),
  });

  const updateUser = useMutation({
    mutationFn: (body: { role?: string; currentPlan?: string | null }) => adminApi.updateUser(userId, body),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ["admin", "users"] }); onUpdate(); },
  });

  const adjustCredits = useMutation({
    mutationFn: () => adminApi.adjustCredits(userId, { delta: Number(creditDelta), reason: creditReason }),
    onSuccess: () => {
      setCreditDelta(""); setCreditReason("");
      queryClient.invalidateQueries({ queryKey: ["admin", "users", userId] });
    },
  });

  const error = (updateUser.error ?? adjustCredits.error) as ApiError | null;

  return (
    <Modal open onClose={onClose} title="User Details" size="lg">
      {isLoading || !data ? (
        <div className="flex justify-center py-8"><Spinner /></div>
      ) : (
        <div className="flex flex-col gap-6 max-h-[70vh] overflow-y-auto">
          {/* User info */}
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="rounded-lg border border-border bg-surface2/30 p-3">
              <p className="text-xs text-fgMuted">Email</p>
              <p className="font-medium text-fg">{data.user.email}</p>
            </div>
            <div className="rounded-lg border border-border bg-surface2/30 p-3">
              <p className="text-xs text-fgMuted">Credit Balance</p>
              <p className="font-medium text-fg">{data.creditBalance} ({Math.floor(data.creditBalance / 100)} videos)</p>
            </div>
          </div>

          {/* Role + plan editors */}
          <div className="grid gap-3 sm:grid-cols-2">
            <Select
              label="Role"
              value={data.user.role}
              onChange={(e) => updateUser.mutate({ role: e.target.value })}
              options={[
                { value: "user", label: "User" },
                { value: "admin", label: "Admin" },
                { value: "superadmin", label: "Super Admin" },
              ]}
            />
            <Select
              label="Plan"
              value={data.user.currentPlan ?? ""}
              onChange={(e) => updateUser.mutate({ currentPlan: e.target.value || null })}
              options={[
                { value: "", label: "No plan" },
                { value: "starter", label: "Starter" },
                { value: "pro", label: "Pro" },
                { value: "agency", label: "Agency" },
              ]}
            />
          </div>

          {/* Credit adjustment */}
          <Card>
            <CardHeader><CardTitle className="text-base">Adjust Credits</CardTitle></CardHeader>
            <CardContent>
              <div className="flex items-end gap-3">
                <div className="w-28">
                  <Input label="Amount" type="number" value={creditDelta} onChange={(e) => setCreditDelta(e.target.value)} placeholder="+100" />
                </div>
                <div className="flex-1">
                  <Input label="Reason" value={creditReason} onChange={(e) => setCreditReason(e.target.value)} placeholder="e.g. refund, bonus" />
                </div>
                <Button
                  size="sm"
                  loading={adjustCredits.isPending}
                  disabled={!creditDelta || !creditReason}
                  onClick={() => adjustCredits.mutate()}
                >
                  <Coins className="h-3.5 w-3.5" /> Apply
                </Button>
              </div>
            </CardContent>
          </Card>

          {error && <p className="text-sm text-danger">{error.message}</p>}

          {/* Recent videos */}
          <Card>
            <CardHeader><CardTitle className="text-base">Recent Videos ({data.videos.length})</CardTitle></CardHeader>
            <CardContent className="p-0">
              {data.videos.length === 0 ? (
                <p className="px-6 py-4 text-sm text-fgMuted">No videos</p>
              ) : (
                <ul className="divide-y divide-border max-h-48 overflow-y-auto">
                  {data.videos.map((v) => (
                    <li key={v.id} className="flex items-center justify-between px-6 py-2.5 text-sm">
                      <span className="text-fgMuted">{new Date(v.createdAt).toLocaleDateString()}</span>
                      <Badge variant={v.status === "complete" ? "success" : v.status === "failed" ? "danger" : "info"}>
                        {v.status.replace(/_/g, " ")}
                      </Badge>
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>

          {/* Recent ledger */}
          <Card>
            <CardHeader><CardTitle className="text-base">Credit Ledger</CardTitle></CardHeader>
            <CardContent className="p-0">
              {data.ledger.length === 0 ? (
                <p className="px-6 py-4 text-sm text-fgMuted">No transactions</p>
              ) : (
                <ul className="divide-y divide-border max-h-48 overflow-y-auto">
                  {data.ledger.map((entry) => (
                    <li key={entry.id} className="flex items-center justify-between px-6 py-2.5 text-sm">
                      <div>
                        <span className="font-medium text-fg capitalize">{entry.reason.replace(/_/g, " ")}</span>
                        <span className="ml-2 text-xs text-fgMuted">{new Date(entry.createdAt).toLocaleString()}</span>
                      </div>
                      <span className={entry.delta >= 0 ? "font-mono text-success" : "font-mono text-fgMuted"}>
                        {entry.delta >= 0 ? "+" : ""}{entry.delta}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </Modal>
  );
}
